import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import apiClient from "@/lib/api-client";
import { extractFullText } from "@/services/pdf-text-extractor";

interface UploadUrlResponse {
  upload_url: string;
  storage_key: string;
  expires_in: number;
}

/**
 * Compute the SHA-256 of a File's bytes as a lowercase hex string.
 * Used to build the per-user, content-addressed storage key so that
 * user uploads never collide with or overwrite the canonical blob.
 */
async function sha256OfFile(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function usePdfUpload(conceptId: string) {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  /** Extract text from a PDF and send it to the backend. Fire-and-forget safe. */
  const extractAndSendText = useCallback(
    async (paperId: string, file: File): Promise<void> => {
      try {
        const text = await extractFullText(file);
        if (text) {
          await apiClient.post(
            `api/concepts/${conceptId}/pdf/extract-text/${paperId}`,
            { json: { text } },
          );
        }
      } catch {
        // Non-fatal — PDF is already uploaded
      }
    },
    [conceptId],
  );

  /** Upload a PDF, extract text, and invalidate cache. Used for single-file uploads.
   *
   * `rightsAttested` MUST be true to succeed — the backend gates /confirm-upload
   * on a rights attestation flag. Callers are responsible for gathering consent
   * (e.g. a confirmation dialog) before invoking this.
   */
  const uploadPdf = useCallback(
    async (
      paperId: string,
      file: File,
      rightsAttested: boolean,
    ): Promise<void> => {
      setIsUploading(true);
      try {
        // 0. Compute sha256 locally so the presigned URL targets the
        //    per-user, content-addressed key.
        const sha256 = await sha256OfFile(file);

        // 1. Get presigned upload URL
        const { upload_url, storage_key } = await apiClient
          .post(`api/concepts/${conceptId}/pdf/upload-url/${paperId}`, {
            json: { sha256 },
          })
          .json<UploadUrlResponse>();

        // 2. PUT file to presigned URL (native fetch — ky adds auth headers we don't want on R2)
        const putRes = await fetch(upload_url, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": "application/pdf" },
        });
        if (!putRes.ok) {
          throw new Error(`Upload failed: ${putRes.status} ${putRes.statusText}`);
        }

        // 3. Confirm upload (server writes paper_pdfs, not Paper.canonical_*).
        await apiClient.post(
          `api/concepts/${conceptId}/pdf/confirm-upload/${paperId}`,
          {
            json: {
              storage_key,
              sha256,
              rights_attestation: rightsAttested,
            },
          },
        );

        // 4. Invalidate cache + toast BEFORE text extraction
        await queryClient.invalidateQueries({
          queryKey: ["papers", conceptId],
        });
        toast.success("PDF uploaded successfully");

        // 5. Extract text fire-and-forget, then refresh cache for full_text_status
        extractAndSendText(paperId, file).then(() =>
          queryClient.invalidateQueries({ queryKey: ["papers", conceptId] }),
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        toast.error("PDF upload failed", { description: msg });
        throw err;
      } finally {
        setIsUploading(false);
      }
    },
    [conceptId, queryClient, extractAndSendText],
  );

  /**
   * Upload a PDF without text extraction or cache invalidation.
   * Used in bulk flows — caller handles invalidation and deferred extraction.
   *
   * `rightsAttested` MUST be true to succeed.
   */
  const uploadPdfFast = useCallback(
    async (
      paperId: string,
      file: File,
      rightsAttested: boolean,
    ): Promise<void> => {
      const sha256 = await sha256OfFile(file);

      const { upload_url, storage_key } = await apiClient
        .post(`api/concepts/${conceptId}/pdf/upload-url/${paperId}`, {
          json: { sha256 },
        })
        .json<UploadUrlResponse>();

      const putRes = await fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "application/pdf" },
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed: ${putRes.status} ${putRes.statusText}`);
      }

      await apiClient.post(
        `api/concepts/${conceptId}/pdf/confirm-upload/${paperId}`,
        {
          json: {
            storage_key,
            sha256,
            rights_attestation: rightsAttested,
          },
        },
      );
    },
    [conceptId],
  );

  /** Invalidate the papers query cache. Call once after a batch of uploads. */
  const invalidatePapers = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["papers", conceptId] }),
    [conceptId, queryClient],
  );

  return { uploadPdf, uploadPdfFast, extractAndSendText, invalidatePapers, isUploading };
}
